import { useState, useEffect, useCallback } from "react";

// ─── useApi — fetch data from the backend ─────────────────────────────────────
export function useApi(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fn());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load };
}

// ─── useToast — toast notification state ─────────────────────────────────────
export function useToast() {
  const [toast, setToast] = useState(null);

  const show = useCallback((msg, type = "default") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const error = useCallback((msg) => show(msg, "error"), [show]);
  const ok    = useCallback((msg) => show(msg, "ok"),    [show]);

  return { toast, show, error, ok };
}

// ─── useDebounce — debounce a value ──────────────────────────────────────────
export function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── useEscKey — call onEsc when Escape is pressed ───────────────────────────
export function useEscKey(onEsc) {
  useEffect(() => {
    if (!onEsc) return;
    const handler = (e) => { if (e.key === "Escape") onEsc(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onEsc]);
}
